// Web Push subscription management (#222). Registers this browser/device with the server so it receives
// notifications even when the dashboard is closed. Everything here degrades quietly: push is a bonus on
// top of the in-app toast + email, and it isn't available in every browser (notably iOS only once the app
// has been added to the Home Screen).
import { api } from './api';

const urlBase64ToUint8Array = (base64: string) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/**
 * Ensure the server has a push subscription for this device, creating one if needed. Safe to call on
 * every load; it only subscribes once notification permission is granted.
 */
export async function syncPushSubscription(): Promise<void> {
  try {
    if (!supported() || Notification.permission !== 'granted') return;
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const { key } = await api.get<{ key: string }>('/api/push/key');
      if (!key) return;
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
    }
    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;
    await api.post('/api/push/subscribe', { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } });
  } catch {
    /* push is a best-effort extra; never block the app on it */
  }
}

/** Remove this device's push subscription (and tell the server to forget it). */
export async function disablePushSubscription(): Promise<void> {
  try {
    if (!supported()) return;
    // No service worker yet (e.g. a dev build) means `ready` never settles: give up after a moment.
    const reg = await Promise.race([navigator.serviceWorker.ready, new Promise<never>((_, no) => setTimeout(() => no(new Error('no service worker')), 1500))]);
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    await sub.unsubscribe().catch(() => undefined);
    await api.post('/api/push/unsubscribe', { endpoint });
  } catch {
    /* ignore */
  }
}
